import React, { useEffect, useState } from "react";
import { api } from "@/api/client";
import TopBar from "@/components/TopBar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Pencil, Download, Eye } from "lucide-react";
import { receiptUrl, isImageMime, isPdf, isHeic } from "@/utils/receipt";

const now = new Date();

export default function History() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [tab, setTab] = useState("attendance");
  const [atts, setAtts] = useState([]); const [exps, setExps] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get(`/attendance/me?year=${year}&month=${month}`).then((r) => setAtts(r.data));
    api.get(`/expenses/me?year=${year}&month=${month}`).then((r) => setExps(r.data));
  }, [year, month]);

  const totalHours = atts.reduce((s, a) => s + (Number(a.working_hours) || 0), 0);
  const totalExp = exps.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const canEdit = (status) => status !== "approved";

  return (
    <div className="shell">
      <TopBar />
      <div className="container">
        <button className="btn btn-outline" onClick={() => nav(-1)} style={{ marginBottom: 12 }}>← {t("common.back")}</button>
        <h1 className="page-title">{t("driver.history")}</h1>
        <div className="row-actions" style={{ marginBottom: 12 }}>
          <input data-testid="hist-year" className="input" style={{ width: 120 }} type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          <select data-testid="hist-month" className="select" style={{ width: 120 }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{i+1}月</option>)}
          </select>
          <button className={`btn ${tab === "attendance" ? "btn-primary" : "btn-outline"}`} onClick={() => setTab("attendance")} data-testid="tab-att">勤怠</button>
          <button className={`btn ${tab === "expense" ? "btn-primary" : "btn-outline"}`} onClick={() => setTab("expense")} data-testid="tab-exp">経費</button>
        </div>

        {tab === "attendance" && (
          <div className="card"><div className="card-body table-wrap">
            <table className="table" data-testid="hist-att-table">
              <thead><tr><th>{t("common.date")}</th><th>{t("driver.startTime")}</th><th>{t("driver.endTime")}</th><th>{t("driver.breakMinutes")}</th><th>{t("driver.workingHours")}</th><th>{t("common.status")}</th><th>{t("common.actions")}</th></tr></thead>
              <tbody>
                {atts.map((a) => (
                  <tr key={a.id}>
                    <td>{a.work_date}</td><td>{a.start_time || "-"}</td><td>{a.end_time || "-"}</td>
                    <td>{a.break_minutes}</td><td>{a.working_hours}</td>
                    <td>
                      <span className={`tag tag-${a.status}`}>{t(`common.${a.status}`)}</span>
                      {a.review_note && (a.status === "rejected" || a.status === "returned") && (
                        <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{a.review_note}</div>
                      )}
                    </td>
                    <td>
                      {canEdit(a.status) && (
                        <button
                          className={a.status === "returned" ? "btn btn-warning" : "btn btn-outline"}
                          style={{ padding: "6px 10px", fontSize: 12 }}
                          onClick={() => nav(`/driver/attendance?date=${a.work_date}`)}
                          data-testid={`hist-att-edit-${a.id}`}
                        >
                          <Pencil size={12} /> {a.status === "returned" ? t("driver.editAndResubmit") : t("common.edit")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {atts.length === 0 && <tr><td colSpan="7" style={{ textAlign: "center", color: "#64748B" }}>{t("driver.noRecords")}</td></tr>}
                <tr><td colSpan="4" style={{ fontWeight: 700 }}>{t("common.total")}</td><td colSpan="3" style={{ fontWeight: 700 }}>{totalHours.toFixed(2)} h</td></tr>
              </tbody>
            </table>
          </div></div>
        )}

        {tab === "expense" && (
          <div className="card"><div className="card-body table-wrap">
            <table className="table" data-testid="hist-exp-table">
              <thead><tr><th>{t("common.date")}</th><th>{t("driver.category")}</th><th>{t("driver.description")}</th><th>{t("driver.amount")}</th><th>{t("common.receipt")}</th><th>{t("common.status")}</th><th>{t("common.actions")}</th></tr></thead>
              <tbody>
                {exps.map((e) => (
                  <tr key={e.id}>
                    <td>{e.expense_date}</td><td>{t(`driver.${e.category}`, e.category)}</td><td>{e.description}</td>
                    <td>¥{Number(e.amount).toLocaleString()}</td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {(e.receipts || []).length === 0 && <span style={{ color: "#94A3B8", fontSize: 12 }}>—</span>}
                        {(e.receipts || []).map((r) => (
                          <div key={r.id} style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                            <button
                              className="btn btn-outline"
                              style={{ padding: "4px 8px", fontSize: 11 }}
                              onClick={() => setPreview(r)}
                              data-testid={`hist-receipt-preview-${r.id}`}
                              title={r.original_name}
                            >
                              <Eye size={11} /> {r.original_name.length > 18 ? r.original_name.slice(0, 15) + "…" : r.original_name}
                            </button>
                            <a
                              className="btn btn-outline"
                              style={{ padding: "4px 8px", fontSize: 11 }}
                              href={receiptUrl(r.file_url)}
                              download={r.original_name || "receipt"}
                              data-testid={`hist-receipt-download-${r.id}`}
                              title={t("common.download")}
                            >
                              <Download size={11} />
                            </a>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`tag tag-${e.status}`}>{t(`common.${e.status}`)}</span>
                      {e.review_note && (e.status === "rejected" || e.status === "returned") && (
                        <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{e.review_note}</div>
                      )}
                    </td>
                    <td>
                      {canEdit(e.status) && (
                        <button
                          className={e.status === "returned" ? "btn btn-warning" : "btn btn-outline"}
                          style={{ padding: "6px 10px", fontSize: 12 }}
                          onClick={() => nav(`/driver/expense?edit=${e.id}`)}
                          data-testid={`hist-exp-edit-${e.id}`}
                        >
                          <Pencil size={12} /> {e.status === "returned" ? t("driver.editAndResubmit") : t("common.edit")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {exps.length === 0 && <tr><td colSpan="7" style={{ textAlign: "center", color: "#64748B" }}>{t("driver.noRecords")}</td></tr>}
                <tr><td colSpan="3" style={{ fontWeight: 700 }}>{t("common.total")}</td><td colSpan="4" style={{ fontWeight: 700 }}>¥{totalExp.toLocaleString()}</td></tr>
              </tbody>
            </table>
          </div></div>
        )}

        {preview && (
          <div className="modal-backdrop" data-testid="hist-receipt-modal" onClick={() => setPreview(null)}>
            <div className="modal-card" onClick={(ev) => ev.stopPropagation()} style={{ maxWidth: 720, maxHeight: "90vh", overflow: "auto" }}>
              <div className="modal-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t("common.receipt")}: {preview.original_name}</span>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <a className="btn btn-primary" style={{ padding: "4px 10px", fontSize: 12 }}
                     href={receiptUrl(preview.file_url)} download={preview.original_name || "receipt"}
                     data-testid="hist-receipt-modal-download">
                    <Download size={12} /> {t("common.download")}
                  </a>
                  <button className="btn btn-outline" style={{ padding: "4px 10px" }} onClick={() => setPreview(null)} data-testid="hist-receipt-modal-close">×</button>
                </div>
              </div>
              {isImageMime(preview.mime_type, preview.original_name) ? (
                <img alt="receipt" src={receiptUrl(preview.file_url)} style={{ width: "100%", borderRadius: 10, display: "block" }} data-testid="hist-receipt-modal-img" />
              ) : isPdf(preview.mime_type, preview.original_name) ? (
                <iframe title="receipt" src={receiptUrl(preview.file_url)} style={{ width: "100%", height: 600, border: 0 }} data-testid="hist-receipt-modal-iframe" />
              ) : (
                <div style={{ padding: 24, textAlign: "center", color: "#64748B" }}>
                  {isHeic(preview.mime_type, preview.original_name)
                    ? "HEIC / HEIF 画像はブラウザで直接表示できません。ダウンロードしてご確認ください。"
                    : t("common.noPreview")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
